import React from "react";

import DatePicker from "react-datepicker";
import { registerLocale } from "react-datepicker";
import Icon from "../icon";

import { isRunTime } from "../../utils/isRunTime";
import { toYYYYMMDD } from "../../utils/datefns";
import { getDayNameLocalized } from "../datePicker/datefns";

// import "react-datepicker/dist/react-datepicker.css";
import "./styles.scss";

const LG_RES = 1024;

const DateRangePicker = ({
  startDatePlaceholder = "Check in",
  endDatePlaceholder = "Check out",
  dateFormat = "yyyy/MM/dd",
  startDate = null,
  endDate = null,
  onChange = () => null,
  locale = "en-US",
  openToDate = new Date(),
  includeDateIntervals,
  excludeDateIntervals,
  minDate = new Date(),
  maxDate = null,
  shouldCloseOnSelect = false,
  availability = false,
  excludeDates,
  onClose = () => null,
  onOpen = () => null,
  destinationColors = false,
  onMonthChange = () => null,
  monthsShown,
  weekdaysBlocked = [],
  customInput = false
}) => {
  const [hoverDate, setHoverDate] = React.useState(null);
  const [device, setDevice] = React.useState(
    isRunTime() && window.innerWidth >= LG_RES ? "desktop" : "mobile"
  );
  React.useEffect(() => {
    const importLocaleFile = async () => {
      const localeToSet = await import(`date-fns/locale/${locale}/index.js`);
      registerLocale(locale, localeToSet);
    };

    importLocaleFile();
  }, [locale]);

  React.useEffect(() => {
    const onResize = () => {
      if (device === "desktop" && window.innerWidth < LG_RES)
        setDevice("mobile");
      if (device === "mobile" && window.innerWidth >= LG_RES)
        setDevice("desktop");
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [device]);

  const checkForWeekDay = (date) => {
    const day = date.getDay();
    if (weekdaysBlocked.length) return !weekdaysBlocked.some(wd => wd === day);
    return true;
  }

  const handleDayMouseEnter = (date) => {
    if (!startDate || endDate) return;

    if (startDate < date) {
      setHoverDate(date);
    } else {
      setHoverDate(startDate);
    }
  };

  const handleDayMouseLeave = () => {
    setHoverDate(null);
  };

  const getAdjustedExcludeDates = () => {
    if (startDate && !endDate) {
      let adjustedExcludeDates = [];
      // If a start date is selected but not an end date, adjust excludeDates
      if (Array.isArray(excludeDates) && typeof excludeDates.filter === "function") {
        adjustedExcludeDates = excludeDates.filter(date => {
          // Get the day before the current date in excludeDates
          const dayBeforeThisExcludedDate = new Date(date);
          dayBeforeThisExcludedDate.setDate(dayBeforeThisExcludedDate.getDate() - 1);
          // Check if the day before this excluded date is also in excludeDates
          return excludeDates.find(excludedDate => excludedDate.getTime() === dayBeforeThisExcludedDate.getTime());
        });
      }

      return adjustedExcludeDates;
    }

    // Return the default excludeDates when both startDate and endDate are selected
    return excludeDates;
  };

  return (
    <div
      className="dateRangePickerWrapper"
    >
      <DatePicker
        selectsRange={true}
        formatWeekDay={(day) => getDayNameLocalized(day, locale)}
        calendarStartDay={1}
        startDate={startDate}
        endDate={endDate}
        onChange={(date) => onChange(date)}
        dateFormat={dateFormat}
        popperClassName={"react-datepicker__popper"}
        popperModifiers={[
          {
            name: "arrow",
            options: {
              padding: ({ popper }) => popper.width / 2,
            },
          },
        ]}
        customInput={
          customInput ? customInput :
          <CustomInput
            locale={locale}
            startDatePlaceholder={startDatePlaceholder}
            endDatePlaceholder={endDatePlaceholder}
            destinationColors={destinationColors}
          />
        }
        popperPlacement={"bottom"}
        showPopperArrow={false}
        monthsShown={monthsShown ? monthsShown : device === "desktop" ? 2 : 1}
        renderCustomHeader={({
          monthDate,
          customHeaderCount,
          decreaseMonth,
          increaseMonth,
        }) => (
          <CustomHeader
            monthsShown={monthsShown ? monthsShown : device === "desktop" ? 2 : 1}
            device={device}
            locale={locale}
            monthDate={monthDate}
            customHeaderCount={customHeaderCount}
            decreaseMonth={decreaseMonth}
            increaseMonth={increaseMonth}
          />
        )}
        disabledKeyboardNavigation
        includeDateIntervals={includeDateIntervals}
        excludeDateIntervals={excludeDateIntervals}
        excludeDates={getAdjustedExcludeDates()}
        locale={locale}
        openToDate={openToDate}
        onCalendarOpen={onOpen}
        onCalendarClose={onClose}
        shouldCloseOnSelect={shouldCloseOnSelect}
        minDate={minDate}
        maxDate={maxDate}
        dayClassName={(date) => {
          if (startDate && startDate.setHours(0,0,0,0) === date.setHours(0,0,0,0)) return 'react-datepicker__day--selected'
          if (hoverDate && startDate &&!endDate && date >= startDate && date <= hoverDate) {
            return 'react-datepicker__day--in-selecting-range';
          }
          if (!availability) return "";
          const found = availability.find(data => {
            if (!data) return null;
            return data.day === toYYYYMMDD(date) && data.available > 0
          })
          if(weekdaysBlocked.length && !checkForWeekDay(date)) return ""
          if(found) return `text-[#088401] bg-[#E9F4E8]`;
          return "";
        }}
        onMonthChange={onMonthChange}
        filterDate={checkForWeekDay}
        onDayMouseEnter={handleDayMouseEnter}
        onDayMouseLeave={handleDayMouseLeave}
      />
    </div>
  );
};

const CustomHeader = (props) => {
  const {
    monthsShown,
    device,
    locale,
    monthDate,
    customHeaderCount,
    decreaseMonth,
    increaseMonth,
  } = props;

  const chevronLeftHidden = monthsShown && monthsShown === 1 ? false : device === "desktop" && customHeaderCount === 1
  const chevronRightHidden = monthsShown && monthsShown === 1 ? false : device === "desktop" && customHeaderCount === 0

  return (
    <div>
      <button
        aria-label="Previous Month"
        className={
          "react-datepicker__navigation react-datepicker__navigation--previous"
        }
        style={
          chevronLeftHidden
            ? { visibility: "hidden" }
            : null
        }
        onClick={decreaseMonth}
      >
        <Icon icon="ChevronLeft" className="h-6 w-6 text-[#242426)]" />
      </button>
      <span className="react-datepicker__current-month">
        {monthDate.toLocaleString(locale, {
          month: "long",
          year: "numeric",
        })}
      </span>
      <button
        aria-label="Next Month"
        className={
          "react-datepicker__navigation react-datepicker__navigation--next"
        }
        style={
          chevronRightHidden
            ? { visibility: "hidden" }
            : null
        }
        onClick={increaseMonth}
      >
        <Icon icon="ChevronRight" className="h-6 w-6 text-[#242426)]" />
      </button>
    </div>
  );
};

const CustomInput = React.forwardRef(
  ({ startDatePlaceholder, endDatePlaceholder, value, onClick , destinationColors = false, locale = "en"}, ref) => {
    const [startDate, endDate] = value.split(" - ");

    const dayFormatter = new Intl.DateTimeFormat(locale, {
      month: 'long', day: 'numeric', weekday: "short"
    })

    const formattedStartDate = startDate ? dayFormatter.format(new Date(startDate)) : "";
    const formattedEndDate = endDate ? dayFormatter.format(new Date(endDate)) : "";

    return (
      <div
        div={ref}
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.nativeEvent.code === "Space" || e.nativeEvent.code === "Enter")
            onClick();
        }}
        tabIndex={0}
        className={`border-2 ${destinationColors ? "border-[color:var(--highlightedSecondaryColor)]" : "border-[#EAEAF2]"} rounded-[8px] cursor-pointer px-6 h-14 flex items-center outline-[color:var(--accentColor)] bg-white`}
      >
        {formattedStartDate ? (
          <span className={`font-[600] text-[0.9375rem] leading-[1.175rem] ${destinationColors ? "text-[color:var(--highlightedSecondaryColor)]" : "text-[#242426]"} capitalize w-full`}>
            {formattedStartDate}
          </span>
        ) : (
          <span className={`font-[600] text-[0.9375rem] leading-[1.175rem] ${destinationColors ? "text-[color:var(--highlightedSecondaryColor)]" :  "text-[#75757D]"} w-full`}>
            {startDatePlaceholder}
          </span>
        )}
        <Icon
          icon="ArrowRight"
          className={`h-4 w-4 min-h-[1rem] min-w-[1rem] ${destinationColors ? "text-[color:var(--highlightedSecondaryColor)]" : "text-[color:var(--accentColor)]"}`}
        />
        {formattedEndDate ? (
          <span className={`font-[600] text-[0.9375rem] leading-[1.175rem] ${destinationColors ? "text-[color:var(--highlightedSecondaryColor)]" : "text-[#242426]"} capitalize w-full flex justify-end`}>
            {formattedEndDate}
          </span>
        ) : (
          <span className={`font-[600] text-[0.9375rem] leading-[1.175rem] ${destinationColors ? "text-[color:var(--highlightedSecondaryColor)]" : "text-[#75757D]"} w-full flex justify-end`}>
            {endDatePlaceholder}
          </span>
        )}
      </div>
    );
  }
);

export default DateRangePicker;
